// Export de toutes les données de l'atelier : un onglet (ou un fichier CSV) par sujet.
// Les temps réels sont reconstitués à partir des pointages des tablettes.
import type { ExportData } from "../data/api.ts";
import {
  MACHINE_TYPE_LABEL, OPERATION_LABEL, OPERATOR_ROLE_LABEL, incidentLabel,
  type Incident, type JobStatus, type ParamValue, type Task, type TaskEvent, type TaskEventKind, type TaskStatus,
} from "../data/types.ts";
import type { Cell, Sheet } from "./xlsx.ts";

const JOB_STATUS_LABEL: Record<JobStatus, string> = {
  a_planifier: "À planifier", planifie: "Planifié", en_cours: "En cours", termine: "Terminé", annule: "Annulé",
};
const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  a_planifier: "À planifier", planifie: "Planifiée", calage: "En calage", production: "En production", pause: "En pause", termine: "Terminée",
};
const EVENT_LABEL: Record<TaskEventKind, string> = {
  calage: "Début du calage", production: "Début de production", pause: "Pause", reprise: "Reprise", termine: "Terminé", quantite: "Quantité saisie",
};

/** Temps réels d'une étape, en minutes, d'après ses pointages. */
export interface RealTimes { calage: number; production: number; pause: number }

const minutes = (a: number, b: number) => Math.max(0, (b - a) / 60_000);
const round1 = (n: number) => Math.round(n * 10) / 10;
const date = (iso: string | null | undefined): Date | null => (iso ? new Date(iso) : null);

/**
 * Additionne le temps passé dans chaque état : calage, production (reprise = production), pause.
 * Une étape encore ouverte compte jusqu'à `now`.
 */
export function realTimes(events: TaskEvent[], now = new Date()): RealTimes {
  const out: RealTimes = { calage: 0, production: 0, pause: 0 };
  const list = [...events].filter((e) => e.kind !== "quantite").sort((a, b) => a.at.localeCompare(b.at));
  let state: keyof RealTimes | null = null;
  let since = 0;
  for (const e of list) {
    const t = new Date(e.at).getTime();
    if (state) out[state] += minutes(since, t);
    state = e.kind === "calage" ? "calage" : e.kind === "production" || e.kind === "reprise" ? "production" : e.kind === "pause" ? "pause" : null;
    since = t;
  }
  if (state) out[state] += minutes(since, now.getTime());
  return { calage: round1(out.calage), production: round1(out.production), pause: round1(out.pause) };
}

function incidentMinutes(i: Incident, now: Date): number {
  return round1(minutes(new Date(i.started_at).getTime(), (i.ended_at ? new Date(i.ended_at) : now).getTime()));
}

function paramsText(p: Record<string, ParamValue> | null | undefined): string {
  if (!p) return "";
  return Object.entries(p)
    .filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && !v.length))
    .map(([k, v]) => `${k} : ${Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "oui" : "non") : v}`)
    .join(" ; ");
}

const sum = (xs: number[]) => round1(xs.reduce((s, x) => s + x, 0));

export function buildSheets(d: ExportData, now = new Date()): Sheet[] {
  const jobs = new Map(d.jobs.map((j) => [j.id, j]));
  const tasks = new Map(d.tasks.map((t) => [t.id, t]));
  const machineName = (id: string | null | undefined) => (id ? d.machines.find((m) => m.id === id)?.name ?? "(machine supprimée)" : "");
  const operatorName = (id: string | null | undefined) => (id ? d.operators.find((o) => o.id === id)?.name ?? "(opérateur supprimé)" : "");

  const eventsByTask = new Map<string, TaskEvent[]>();
  for (const e of d.events) eventsByTask.set(e.task_id, [...(eventsByTask.get(e.task_id) || []), e]);
  const incidentsByTask = new Map<string, Incident[]>();
  for (const i of d.incidents) if (i.task_id) incidentsByTask.set(i.task_id, [...(incidentsByTask.get(i.task_id) || []), i]);

  const times = new Map<string, RealTimes>();
  for (const t of d.tasks) times.set(t.id, realTimes(eventsByTask.get(t.id) || [], now));
  const aleaMin = (taskId: string) => sum((incidentsByTask.get(taskId) || []).map((i) => incidentMinutes(i, now)));

  const taskOrder = (a: Task, b: Task) =>
    (jobs.get(a.job_id)?.of_number || "").localeCompare(jobs.get(b.job_id)?.of_number || "", "fr", { numeric: true }) || a.seq - b.seq;

  // --- Jobs ------------------------------------------------------------------
  const jobSheet: Sheet = {
    name: "Jobs",
    headers: [
      "N° OF", "Client", "Titre", "Quantité", "Priorité", "Statut", "Échéance", "Feuilles disponibles le",
      "Étapes", "Étapes terminées", "Calage réel (min)", "Production réelle (min)", "Pauses (min)",
      "Aléas", "Durée des aléas (min)", "Gâche", "Créé le", "Modifié le", "Notes",
    ],
    rows: [...d.jobs].sort((a, b) => a.due_at.localeCompare(b.due_at)).map((j) => {
      const ts = d.tasks.filter((t) => t.job_id === j.id);
      const rt = ts.map((t) => times.get(t.id)!);
      const inc = ts.flatMap((t) => incidentsByTask.get(t.id) || []);
      return [
        j.of_number, j.client, j.title, j.quantity, j.priority === "urgent" ? "Urgent" : "Normal", JOB_STATUS_LABEL[j.status] || j.status,
        date(j.due_at), date(j.sheets_ready_at),
        ts.length, ts.filter((t) => t.status === "termine").length,
        sum(rt.map((r) => r.calage)), sum(rt.map((r) => r.production)), sum(rt.map((r) => r.pause)),
        inc.length, sum(inc.map((i) => incidentMinutes(i, now))), ts.reduce((s, t) => s + (t.waste_qty || 0), 0),
        date(j.created_at), date(j.updated_at), j.notes,
      ];
    }),
  };

  // --- Étapes (tâches) -----------------------------------------------------------
  const taskSheet: Sheet = {
    name: "Tâches",
    headers: [
      "N° OF", "Client", "Job", "Étape", "Opération", "Type de pli", "Format d'entrée", "Format de sortie", "Papier", "Grammage",
      "Pages", "Conditionnement", "Réglages", "Statut", "Machine", "Conducteur attribué",
      "Début prévu", "Fin prévue", "Calage prévu (min)", "Production prévue (min)", "Durée forcée (min)", "Verrouillée",
      "Machine publiée", "Début publié", "Début réel", "Fin réelle",
      "Calage réel (min)", "Production réelle (min)", "Pauses (min)", "Aléas (min)",
      "Quantité produite", "Gâche", "Cadence réelle (par heure)",
    ],
    rows: [...d.tasks].sort(taskOrder).map((t) => {
      const j = jobs.get(t.job_id);
      const r = times.get(t.id)!;
      const rate = r.production > 0 && t.produced_qty ? Math.round((t.produced_qty / r.production) * 60) : null;
      return [
        j?.of_number ?? "", j?.client ?? "", j?.title ?? "", t.seq, OPERATION_LABEL[t.operation] || t.operation, t.fold_type,
        t.format_in, t.format_out, t.paper, t.grammage, t.pages, t.packaging, paramsText(t.params),
        TASK_STATUS_LABEL[t.status] || t.status, machineName(t.machine_id), operatorName(t.operator_id),
        date(t.planned_start), date(t.planned_end), t.setup_min, t.run_min, t.manual_run_min ?? null, t.locked ? "oui" : "non",
        machineName(t.pub_machine_id), date(t.pub_start), date(t.started_at), date(t.finished_at),
        r.calage, r.production, r.pause, aleaMin(t.id),
        t.produced_qty, t.waste_qty, rate,
      ];
    }),
  };

  // --- Aléas ---------------------------------------------------------------------
  const incidentSheet: Sheet = {
    name: "Aléas",
    headers: ["Début", "Fin", "Durée (min)", "État", "Machine", "Nature", "Commentaire", "N° OF", "Client", "Opération", "Opérateur"],
    rows: [...d.incidents].sort((a, b) => a.started_at.localeCompare(b.started_at)).map((i) => {
      const t = i.task_id ? tasks.get(i.task_id) : undefined;
      const j = t ? jobs.get(t.job_id) : undefined;
      return [
        date(i.started_at), date(i.ended_at), incidentMinutes(i, now), i.ended_at ? "Terminé" : "En cours",
        machineName(i.machine_id), incidentLabel(i.kind), i.comment, j?.of_number ?? "", j?.client ?? "",
        t ? OPERATION_LABEL[t.operation] || t.operation : "", operatorName(i.operator_id),
      ];
    }),
  };

  // --- Temps (pointages des tablettes) --------------------------------------------
  const sortedEvents = [...d.events].sort((a, b) => a.at.localeCompare(b.at));
  const nextOf = new Map<string, TaskEvent>();
  for (const evs of eventsByTask.values()) {
    const s = evs.filter((e) => e.kind !== "quantite").sort((a, b) => a.at.localeCompare(b.at));
    s.forEach((e, k) => { if (s[k + 1]) nextOf.set(e.id, s[k + 1]); });
  }
  const timeSheet: Sheet = {
    name: "Temps",
    headers: ["Date et heure", "Machine", "Opérateur", "N° OF", "Client", "Étape", "Opération", "Pointage", "Quantité", "Durée jusqu'au pointage suivant (min)"],
    rows: sortedEvents.map((e) => {
      const t = tasks.get(e.task_id);
      const j = t ? jobs.get(t.job_id) : undefined;
      const next = nextOf.get(e.id);
      const open = !next && e.kind !== "termine" && e.kind !== "quantite" && t?.status !== "termine";
      const dur = e.kind === "termine" || e.kind === "quantite" ? null
        : next ? round1(minutes(new Date(e.at).getTime(), new Date(next.at).getTime()))
        : open ? round1(minutes(new Date(e.at).getTime(), now.getTime())) : null;
      return [
        date(e.at), machineName(e.machine_id), operatorName(e.operator_id), j?.of_number ?? "", j?.client ?? "",
        t?.seq ?? null, t ? OPERATION_LABEL[t.operation] || t.operation : "", EVENT_LABEL[e.kind] || e.kind, e.qty, dur,
      ];
    }),
  };

  // --- Synthèse par machine ---------------------------------------------------------
  const machineSheet: Sheet = {
    name: "Machines",
    headers: [
      "Machine", "Type", "Active", "Cadence prévue (par heure)", "Calage prévu (min)", "Étapes terminées",
      "Calage réel (min)", "Production réelle (min)", "Pauses (min)", "Aléas", "Durée des aléas (min)", "Quantité produite", "Gâche",
    ],
    rows: d.machines.map((m) => {
      // une étape compte pour la machine où elle a été pointée (à défaut : celle du planning)
      const ts = d.tasks.filter((t) => (eventsByTask.get(t.id)?.[0]?.machine_id ?? t.machine_id) === m.id);
      const rt = ts.map((t) => times.get(t.id)!);
      const inc = d.incidents.filter((i) => i.machine_id === m.id);
      return [
        m.name, MACHINE_TYPE_LABEL[m.type] || m.type, m.active ? "oui" : "non", m.speed_per_hour, m.setup_min,
        ts.filter((t) => t.status === "termine").length,
        sum(rt.map((r) => r.calage)), sum(rt.map((r) => r.production)), sum(rt.map((r) => r.pause)),
        inc.length, sum(inc.map((i) => incidentMinutes(i, now))),
        ts.reduce((s, t) => s + (t.produced_qty || 0), 0), ts.reduce((s, t) => s + (t.waste_qty || 0), 0),
      ];
    }),
  };

  // --- Opérateurs -------------------------------------------------------------------
  const operatorSheet: Sheet = {
    name: "Opérateurs",
    headers: ["Opérateur", "Rôle", "Actif", "Postes", "Pointages", "Aléas signalés"],
    rows: d.operators.map((o) => [
      o.name, OPERATOR_ROLE_LABEL[o.role] || o.role, o.active ? "oui" : "non", o.machine_ids.map(machineName).join(", "),
      d.events.filter((e) => e.operator_id === o.id).length, d.incidents.filter((i) => i.operator_id === o.id).length,
    ] as Cell[]),
  };

  return [jobSheet, taskSheet, incidentSheet, timeSheet, machineSheet, operatorSheet];
}

/** Nom de fichier : ordopli-<imprimerie>-<aaaa-mm-jj>. */
export function exportFileName(orgName: string, now = new Date()): string {
  const slug = orgName.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "atelier";
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `ordopli-${slug}-${day}`;
}
